/**
 * WidgetChart — renders a KPI builder spec + computed preview using Recharts.
 * Kept intentionally small so both the drawer preview and the dashboard tile
 * share the exact same render path.
 */
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const PALETTE = ['#F26B1F', '#8b5cf6', '#0ea5e9', '#22c55e', '#f59e0b',
                 '#ef4444', '#0e7c6b', '#1e4e8c', '#7b3fa1', '#64748b'];

const AXIS = { fontSize: 10, fill: 'var(--text-muted, #64748b)' };

const fmt = (v, unit) => {
  if (v === null || v === undefined) return '—';
  if (unit === 'kg') {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}t`;
    return `${Math.round(v)} kg`;
  }
  if (unit === '%') return `${v}%`;
  return typeof v === 'number' ? v.toLocaleString() : v;
};

export function WidgetChart({ spec, data, compact }) {
  const meta = data?.meta || {};
  const series = data?.series || [];
  const unit = meta.unit || '';

  // ── Scalar / KPI tile ─────────────────────────────────────────────
  if (spec.chart_type === 'kpi_tile' || meta.kind === 'scalar') {
    const value = series[0]?.value ?? 0;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center"
           data-testid="widget-kpi-tile">
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-60">{spec.metric}</div>
        <div className="mt-2 font-bold" style={{ color: '#F26B1F', fontSize: compact ? 32 : 48 }}>
          {fmt(value, unit)}
        </div>
        <div className="text-xs opacity-70 mt-1">{spec.title}</div>
      </div>
    );
  }

  // ── Trend ─────────────────────────────────────────────────────────
  if (meta.kind === 'trend') {
    const Wrap = spec.chart_type === 'area' ? AreaChart : LineChart;
    const El   = spec.chart_type === 'area' ? Area : Line;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <Wrap data={series} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e2e8f0)" />
          <XAxis dataKey="label" tick={AXIS} />
          <YAxis tick={AXIS} tickFormatter={(v) => fmt(v, unit)} />
          <Tooltip formatter={(v) => fmt(v, unit)} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <El type="monotone" dataKey="target"   stroke="#8b5cf6" strokeWidth={2} fill="#8b5cf655" dot={{ r: 3 }} />
          <El type="monotone" dataKey="actual"   stroke="#F26B1F" strokeWidth={2} fill="#F26B1F55" dot={{ r: 3 }} />
          <El type="monotone" dataKey="forecast" stroke="#0ea5e9" strokeWidth={2} fill="#0ea5e955" strokeDasharray="4 4" dot={{ r: 3 }} />
        </Wrap>
      </ResponsiveContainer>
    );
  }

  // ── Pie / Donut ───────────────────────────────────────────────────
  if (spec.chart_type === 'pie' || spec.chart_type === 'donut') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={series} dataKey="value" nameKey="label" outerRadius="80%"
               innerRadius={spec.chart_type === 'donut' ? '55%' : 0}
               label={({ label, value }) => `${label}: ${fmt(value, unit)}`}
               labelLine={false}>
            {series.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmt(v, unit)} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // ── Bar (horizontal or vertical) — default ────────────────────────
  const horizontal = spec.chart_type === 'horizontal_bar';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={series}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, left: horizontal ? 60 : -8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e2e8f0)" />
        {horizontal ? (
          <>
            <XAxis type="number" tick={AXIS} tickFormatter={(v) => fmt(v, unit)} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={80} />
          </>
        ) : (
          <>
            <XAxis dataKey="label" tick={AXIS} />
            <YAxis tick={AXIS} tickFormatter={(v) => fmt(v, unit)} />
          </>
        )}
        <Tooltip formatter={(v) => fmt(v, unit)} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {series.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
